import React, { ComponentProps } from 'react';
import { Box, Typography, TextField, SxProps } from '@mui/material';

type MuiTextFieldProps = ComponentProps<typeof TextField>;

interface DosenInputProps extends MuiTextFieldProps {
  label?: string;
  maxChars?: number;
  minChars?: number;
  inputSx?: SxProps;
}

export default function DosenInput({
  label,
  value = '',
  onChange,
  placeholder,
  multiline,
  rows,
  disabled,
  type,
  helperText,
  maxChars,
  minChars,
  sx = {},
  inputSx = {},
  InputProps,
  ...rest
}: DosenInputProps) {
  const length = String(value)?.length || 0;
  const showCounter = maxChars !== undefined || minChars !== undefined;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        fontFamily: 'Inter !important',
        ...sx
      }}
    >
      {(label || showCounter) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {label && (
            <Typography
              sx={{
                color: 'var(--Text-text-primary, #FDFDFD)',
                fontSize: '14px',
                fontFamily: 'Inter !important',
                fontWeight: 500
              }}
            >
              {label}
            </Typography>
          )}

          {showCounter && (
            <Typography
              sx={{
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: '12px',
                fontFamily: 'Inter !important'
              }}
            >
              {maxChars ? `${length}/${maxChars}` : `${length}/${minChars}+`}
            </Typography>
          )}
        </Box>
      )}

      <TextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        disabled={disabled}
        type={type}
        helperText={helperText}
        InputProps={{
          ...(InputProps || {}),
          inputProps: {
            ...(InputProps?.inputProps || {}),
            maxLength: maxChars
          }
        }}
        fullWidth
        {...rest}
        sx={{
          fontFamily: 'Inter !important',

          /** ROOT – Figma: Fill-Transparent-trans-normal-7/70, Stroke-Border-border-highlight */
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--Fill-Transparent-trans-normal-7-70, rgba(0,0,0,0.70)) !important',
            borderRadius: '6px',
            fontFamily: 'Inter !important',
            '&.MuiInputBase-multiline': {
              padding: '0 !important'
            },

            '& fieldset': {
              borderColor: 'var(--Stroke-Border-border-highlight, #868686) !important'
            },
            '&:hover fieldset': {
              borderColor: 'var(--Stroke-Form-Input-border-light, #868686) !important'
            },
            '&.Mui-focused fieldset': {
              borderColor: 'var(--Stroke-icon-highlight, #FFFFFF) !important'
            },
            '&.Mui-disabled': {
              backgroundColor: 'var(--Fill-Transparent-trans-normal-7-70, rgba(0,0,0,0.70)) !important',
              opacity: 0.6
            }
          },

          /** INPUT */
          '& .MuiInputBase-input': {
            backgroundColor: 'transparent !important',
            color: 'var(--Text-text-primary, #FDFDFD) !important',
            padding: '10px 12px',
            fontSize: '14px',
            fontFamily: 'Inter !important'
          },

          /** PLACEHOLDER */
          '& .MuiInputBase-input::placeholder': {
            color: 'var(--Text-text-tertiary, #868686) !important',
            opacity: 1,
            fontFamily: 'Inter !important'
          },

          /** MULTILINE */
          '& .MuiOutlinedInput-inputMultiline': {
            backgroundColor: 'transparent !important',
            color: 'var(--Text-text-primary, #FDFDFD) !important',
            padding: '10px 12px',
            fontFamily: 'Inter !important'
          },

          /** ROOT BASE */
          '& .MuiInputBase-root': {
            backgroundColor: 'var(--Fill-Transparent-trans-normal-7-70, rgba(0,0,0,0.70)) !important',
            fontFamily: 'Inter !important'
          },

          /** HELPER TEXT – Figma: Text-text-secondary (red when error) */
          '& .MuiFormHelperText-root': {
            color: 'var(--Text-text-secondary, #B6B6B6) !important',
            fontSize: '12px',
            marginLeft: '4px',
            fontFamily: 'Inter !important'
          },
          '& .MuiFormHelperText-root.Mui-error': {
            color: 'var(--UI-Error, #F44336) !important'
          },

          /** LABEL */
          '& .MuiInputLabel-root': {
            color: 'var(--Text-text-primary, #FDFDFD) !important',
            fontFamily: 'Inter !important'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'var(--Text-text-primary, #FDFDFD) !important',
            fontFamily: 'Inter !important'
          },

          '& .MuiTypography-root': {
            fontFamily: 'Inter !important'
          },

          ...inputSx
        }}
      />
    </Box>
  );
}
